import React, { useEffect, useRef } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { getImageUrl } from '../api';

const ThankYou = () => {
    const location  = useLocation();
    const navigate  = useNavigate();
    const confettiRef = useRef(null);

    // Pull state passed from Checkout on success
    const {
        paymentId = '',
        orderId   = '',
        amount    = 0,
        name      = '',
        email     = '',
        items     = [],
        coupon    = null,
        discount  = 0,
    } = location.state || {};

    // If someone lands here without state, redirect home
    useEffect(() => {
        if (!location.state) {
            navigate('/', { replace: true });
        }
    }, [location.state, navigate]);

    // Simple confetti burst on mount
    useEffect(() => {
        const canvas = confettiRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        canvas.width  = canvas.offsetWidth;
        canvas.height = canvas.offsetHeight;

        const colors  = ['#185FA5', '#F57C00', '#3B6D11', '#E53935', '#7b1fa2', '#00838f'];
        const pieces  = Array.from({ length: 90 }, () => ({
            x:    Math.random() * canvas.width,
            y:    Math.random() * -canvas.height,
            w:    6 + Math.random() * 8,
            h:    10 + Math.random() * 6,
            color: colors[Math.floor(Math.random() * colors.length)],
            vx:   (Math.random() - 0.5) * 2.5,
            vy:   2 + Math.random() * 3,
            angle: Math.random() * 360,
            spin:  (Math.random() - 0.5) * 6,
            alpha: 1,
        }));

        let frame;
        const draw = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            pieces.forEach(p => {
                p.x     += p.vx;
                p.y     += p.vy;
                p.angle += p.spin;
                if (p.y > canvas.height * 0.55) p.alpha = Math.max(0, p.alpha - 0.025);
                ctx.save();
                ctx.globalAlpha = p.alpha;
                ctx.translate(p.x, p.y);
                ctx.rotate((p.angle * Math.PI) / 180);
                ctx.fillStyle = p.color;
                ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
                ctx.restore();
            });
            if (pieces.some(p => p.alpha > 0)) frame = requestAnimationFrame(draw);
        };
        frame = requestAnimationFrame(draw);
        return () => cancelAnimationFrame(frame);
    }, []);

    if (!location.state) return null;

    const orderDate = new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });

    return (
        <section className="ty-wrap">
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

                *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

                .ty-wrap {
                    min-height: 100vh;
                    background: #F0F6FF;
                    font-family: 'Plus Jakarta Sans', sans-serif;
                    padding: 40px 16px 60px;
                }

                /* ── Hero card ── */
                .ty-hero {
                    position: relative;
                    max-width: 640px;
                    margin: 0 auto 20px;
                    background: linear-gradient(135deg, #185FA5 0%, #0C447C 100%);
                    border-radius: 20px;
                    padding: 40px 32px 32px;
                    text-align: center;
                    overflow: hidden;
                    box-shadow: 0 8px 40px rgba(24,95,165,0.28);
                }

                .ty-confetti {
                    position: absolute;
                    inset: 0;
                    width: 100%;
                    height: 100%;
                    pointer-events: none;
                }

                .ty-tick-ring {
                    width: 74px;
                    height: 74px;
                    border-radius: 50%;
                    background: rgba(255,255,255,0.15);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto 18px;
                    animation: ty-pop 0.5s cubic-bezier(.34,1.56,.64,1) both;
                }
                @keyframes ty-pop {
                    from { transform: scale(0); opacity: 0; }
                    to   { transform: scale(1); opacity: 1; }
                }

                .ty-tick {
                    width: 52px;
                    height: 52px;
                    border-radius: 50%;
                    background: #fff;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .ty-tick svg { width: 26px; height: 26px; }

                .ty-hero-title {
                    font-size: 24px;
                    font-weight: 800;
                    color: #fff;
                    margin-bottom: 6px;
                    animation: ty-fade-up 0.5s 0.15s ease both;
                }
                .ty-hero-sub {
                    font-size: 13px;
                    color: rgba(255,255,255,0.78);
                    line-height: 1.6;
                    animation: ty-fade-up 0.5s 0.25s ease both;
                }
                @keyframes ty-fade-up {
                    from { opacity: 0; transform: translateY(12px); }
                    to   { opacity: 1; transform: translateY(0); }
                }

                /* ── Pill badges ── */
                .ty-pills {
                    display: flex;
                    justify-content: center;
                    gap: 10px;
                    flex-wrap: wrap;
                    margin-top: 22px;
                    animation: ty-fade-up 0.5s 0.35s ease both;
                }
                .ty-pill {
                    background: rgba(255,255,255,0.18);
                    border: 1px solid rgba(255,255,255,0.3);
                    border-radius: 50px;
                    padding: 6px 14px;
                    font-size: 12px;
                    font-weight: 600;
                    color: #fff;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                }

                /* ── Content cards ── */
                .ty-card {
                    max-width: 640px;
                    margin: 0 auto 16px;
                    background: #fff;
                    border: 0.5px solid #B5D4F4;
                    border-radius: 16px;
                    padding: 22px 24px;
                    animation: ty-fade-up 0.5s ease both;
                }

                .ty-card-title {
                    font-size: 13px;
                    font-weight: 700;
                    color: #0C447C;
                    text-transform: uppercase;
                    letter-spacing: 0.07em;
                    margin-bottom: 16px;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }
                .ty-card-title::before {
                    content: '';
                    width: 3px;
                    height: 14px;
                    background: #F57C00;
                    border-radius: 4px;
                    display: inline-block;
                }

                /* ── Info grid ── */
                .ty-info-grid {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 12px;
                }
                @media (max-width: 480px) { .ty-info-grid { grid-template-columns: 1fr; } }

                .ty-info-cell {
                    background: #F7FBFF;
                    border: 0.5px solid #B5D4F4;
                    border-radius: 10px;
                    padding: 12px 14px;
                }
                .ty-info-label {
                    font-size: 11px;
                    color: #888;
                    font-weight: 500;
                    margin-bottom: 4px;
                }
                .ty-info-value {
                    font-size: 13px;
                    color: #1a1a2e;
                    font-weight: 600;
                    word-break: break-all;
                }
                .ty-info-value.mono {
                    font-family: 'Courier New', monospace;
                    font-size: 11px;
                    color: #185FA5;
                }

                /* ── Product list ── */
                .ty-item-row {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    padding: 10px 0;
                    border-bottom: 0.5px solid #e8f0fb;
                }
                .ty-item-row:last-child { border-bottom: none; }

                .ty-item-img {
                    width: 46px;
                    height: 46px;
                    object-fit: contain;
                    border-radius: 8px;
                    background: #E6F1FB;
                    border: 0.5px solid #B5D4F4;
                    padding: 3px;
                    flex-shrink: 0;
                }
                .ty-item-img-placeholder {
                    width: 46px;
                    height: 46px;
                    border-radius: 8px;
                    background: #E6F1FB;
                    border: 0.5px solid #B5D4F4;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 18px;
                    flex-shrink: 0;
                }
                .ty-item-name {
                    font-size: 13px;
                    font-weight: 600;
                    color: #1a1a2e;
                    flex: 1;
                    min-width: 0;
                }
                .ty-item-meta {
                    font-size: 11px;
                    color: #999;
                    margin-top: 2px;
                }
                .ty-item-price {
                    font-size: 13px;
                    font-weight: 700;
                    color: #F57C00;
                    white-space: nowrap;
                }

                /* ── Summary rows ── */
                .ty-summary-row {
                    display: flex;
                    justify-content: space-between;
                    padding: 7px 0;
                    font-size: 13px;
                    border-bottom: 0.5px solid #e8f0fb;
                }
                .ty-summary-row:last-child { border-bottom: none; }
                .ty-sum-label { color: #555; }
                .ty-sum-value { font-weight: 600; color: #1a1a2e; }
                .ty-sum-green { color: #3B6D11; }
                .ty-sum-total-label { font-size: 15px; font-weight: 700; color: #1a1a2e; }
                .ty-sum-total-value { font-size: 18px; font-weight: 800; color: #185FA5; }
                .ty-free-badge { font-size: 11px; font-weight: 600; color: #3B6D11; background: #EAF3DE; padding: 2px 10px; border-radius: 20px; }
                .ty-divider { border: none; border-top: 1px dashed #B5D4F4; margin: 6px 0; }

                /* ── Tracking stepper ── */
                .ty-steps {
                    display: flex;
                    justify-content: space-between;
                    position: relative;
                    padding: 0 8px;
                }
                .ty-steps::before {
                    content: '';
                    position: absolute;
                    top: 18px;
                    left: 32px;
                    right: 32px;
                    height: 2px;
                    background: #B5D4F4;
                    z-index: 0;
                }
                .ty-step {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 8px;
                    z-index: 1;
                    flex: 1;
                }
                .ty-step-dot {
                    width: 36px;
                    height: 36px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 15px;
                    border: 2px solid #B5D4F4;
                    background: #fff;
                    transition: all 0.3s;
                }
                .ty-step-dot.done {
                    background: #185FA5;
                    border-color: #185FA5;
                    color: #fff;
                    box-shadow: 0 0 0 4px rgba(24,95,165,0.12);
                }
                .ty-step-dot.active {
                    background: #F57C00;
                    border-color: #F57C00;
                    color: #fff;
                    box-shadow: 0 0 0 4px rgba(245,124,0,0.15);
                    animation: ty-pulse 1.4s infinite;
                }
                @keyframes ty-pulse {
                    0%, 100% { box-shadow: 0 0 0 4px rgba(245,124,0,0.15); }
                    50%       { box-shadow: 0 0 0 8px rgba(245,124,0,0.08); }
                }
                .ty-step-label {
                    font-size: 10px;
                    font-weight: 600;
                    color: #888;
                    text-align: center;
                    line-height: 1.3;
                }
                .ty-step-label.active { color: #F57C00; }
                .ty-step-label.done   { color: #185FA5; }

                /* ── CTA buttons ── */
                .ty-cta-group {
                    max-width: 640px;
                    margin: 0 auto;
                    display: flex;
                    gap: 12px;
                    flex-wrap: wrap;
                    animation: ty-fade-up 0.5s 0.6s ease both;
                }
                .ty-btn-primary {
                    flex: 1;
                    min-width: 160px;
                    // background: #185FA5;
                    color: #185FA5;
                    border: none;
                    // border-radius: 50px;
                    padding: 14px 24px;
                    font-size: 14px;
                    font-weight: 700;
                    cursor: pointer;
                    text-align: center;
                    text-decoration: none;
                    display: block;
                    transition: background 0.18s, transform 0.15s;
                    font-family: 'Plus Jakarta Sans', sans-serif;
                }
                // .ty-btn-primary:hover { background: #0C447C; transform: translateY(-1px); color: #fff; }

                .ty-btn-ghost {
                    flex: 1;
                    min-width: 160px;
                    // background: #fff;
                    color: #185FA5;
                    // border: 1.5px solid #185FA5;
                    // border-radius: 50px;
                    padding: 14px 24px;
                    font-size: 14px;
                    font-weight: 700;
                    cursor: pointer;
                    text-align: center;
                    text-decoration: none;
                    display: block;
                    transition: all 0.18s;
                    font-family: 'Plus Jakarta Sans', sans-serif;
                }
                // .ty-btn-ghost:hover { background: #185FA5; color: #fff; }

                /* ── Logo bar ── */
                .ty-logo-bar {
                    max-width: 640px;
                    margin: 20px auto 0;
                    text-align: center;
                    font-size: 12px;
                    color: #aaa;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                }
                .ty-logo-k {
                    width: 20px;
                    height: 20px;
                    background: #F57C00;
                    border-radius: 5px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    color: #fff;
                    font-size: 11px;
                    font-weight: 800;
                }
            `}</style>

            {/* ── Hero ── */}
            <div className="ty-hero">
                <canvas ref={confettiRef} className="ty-confetti" />

                <div className="ty-tick-ring">
                    <div className="ty-tick">
                        <svg viewBox="0 0 52 52" fill="none">
                            <circle cx="26" cy="26" r="25" fill="#185FA5" />
                            <path d="M14 27l9 9 16-17" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </div>
                </div>

                <h1 className="ty-hero-title">Order Confirmed! 🎉</h1>
                <p className="ty-hero-sub">
                    Thank you{name ? `, ${name.split(' ')[0]}` : ''}! Your payment was successful and your order is being processed.<br />
                    {email && <span>A confirmation will be sent to <strong style={{ color: '#fff' }}>{email}</strong></span>}
                </p>

                <div className="ty-pills">
                    <div className="ty-pill">✅ Payment Verified</div>
                    <div className="ty-pill">📦 Order Placed</div>
                    <div className="ty-pill">🚚 FREE Delivery</div>
                </div>
            </div>

   {/* ── Order Items ── */}
{items.length > 0 && (
    <div className="ty-card" style={{ animationDelay: '0.4s' }}>
        <div className="ty-card-title">Items Ordered</div>
        {items.map((item, i) => (
            <div className="ty-item-row" key={i}>
                {item.img ? (
                    <img
                        src={getImageUrl(item.img)}
                        alt={item.name}
                        className="ty-item-img"
                        onError={e => { e.target.style.display = 'none'; }}
                    />
                ) : (
                    <div className="ty-item-img-placeholder">🛍️</div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="ty-item-name">{item.name}</div>
                    {item.weight && <div className="ty-item-meta">{item.weight}</div>}
                    <div className="ty-item-meta">Qty: {item.quantity || 1}</div>
                </div>
                <span className="ty-item-price">
                    ₹{((parseFloat(item.price) || 0) * (item.quantity || 1)).toFixed(2)}
                </span>
            </div>
        ))}

        <hr className="ty-divider" />

        <div className="ty-summary-row">
            <span className="ty-sum-label">Subtotal</span>
            <span className="ty-sum-value">
                ₹{items.reduce((s, i) => s + (parseFloat(i.price) || 0) * (i.quantity || 1), 0).toFixed(2)}
            </span>
        </div>
        <div className="ty-summary-row">
            <span className="ty-sum-label">Delivery</span>
            <span className="ty-free-badge">FREE</span>
        </div>
        {coupon && (
            <div className="ty-summary-row">
                <span className="ty-sum-label">Coupon ({coupon.code})</span>
                <span className="ty-sum-green">−₹{parseFloat(discount).toFixed(2)}</span>
            </div>
        )}
        <hr className="ty-divider" />
        <div className="ty-summary-row">
            <span className="ty-sum-total-label">Total Paid</span>
            <span className="ty-sum-total-value">₹{parseFloat(amount).toFixed(2)}</span>
        </div>
    </div>
)}{/* ── Payment & Order Info ── */}
<div className="ty-card" style={{ animationDelay: '0.3s' }}>
    <div className="ty-card-title">Payment Details</div>
    <div className="ty-info-grid">
        <div className="ty-info-cell">
            <div className="ty-info-label">Payment ID</div>
            <div className="ty-info-value mono">{paymentId}</div>
        </div>
        <div className="ty-info-cell">
            <div className="ty-info-label">Order ID</div>
            <div className="ty-info-value mono">{orderId}</div>
        </div>
        <div className="ty-info-cell">
            <div className="ty-info-label">Order Date</div>
            <div className="ty-info-value">{orderDate}</div>
        </div>
        <div className="ty-info-cell">
            <div className="ty-info-label">Amount Paid</div>
            <div className="ty-info-value" style={{ color: '#185FA5' }}>₹{parseFloat(amount).toFixed(2)}</div>
        </div>
    </div>
</div>

            {/* ── CTA buttons ── */}
            <div className="ty-cta-group">
                <Link to="/" className="ty-btn-primary">← Continue Shopping</Link>
                <Link to="/orders" className="ty-btn-ghost">My Orders →</Link>
            </div>

            <div className="ty-logo-bar">
                <div className="ty-logo-k">K</div>
                <span>Kalpanamart — Thank you for shopping with us!</span>
            </div>
        </section>
    );
};

export default ThankYou;